import { ChevronRight, FileText, Loader2, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";

import type { TabOption } from "@/components/data-table/tab-filters";
import type { PublicContract } from "@/types/public-contract";

import {
	getPublicContractBuyerInfo,
	getPublicContractBuyerName,
	getPublicContractCode,
	getPublicContractTotal,
	usePublicContracts
} from "@/api/public-contract-query";
import { GenericDataTable } from "@/components/data-table/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import useDebounce from "@/hooks/use-debounce";
import { useFilters } from "@/hooks/use-filters";
import { cn, formatCurrency, sanitizeParams } from "@/lib/utils";
import { Filters } from "@/types/filters";
import { sortByToState, stateToSortBy } from "@/types/table-sort";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";

const BaseParams = z.object({
	status: z.string().optional(),
	q: z.string().optional(),
	pageIndex: z.coerce.number().optional(),
	pageSize: z.coerce.number().optional(),
	sortBy: z.string().regex(/^.+\.(asc|desc)$/).optional()
});

export const InvoiceParams = Filters(BaseParams);

export const Route = createFileRoute("/vendor/invoices/")({
	component: RouteComponent,
	validateSearch: InvoiceParams
});

const STATUS_LABELS: Record<string, string> = {
	PENDING: "Pending",
	PAID: "Paid",
	SHIPED: "Shipped",
	COMPLETED: "Completed",
	ABANDONED: "Abandoned",
	EXPIRED: "Expired",
	CANCELED: "Canceled"
};

const STATUS_CLASSES: Record<string, string> = {
	PENDING: "bg-yellow-200 text-yellow-800",
	PAID: "bg-blue-200 text-blue-800",
	SHIPED: "bg-indigo-200 text-indigo-800",
	COMPLETED: "bg-green-200 text-green-800",
	ABANDONED: "bg-orange-200 text-orange-800",
	EXPIRED: "bg-gray-200 text-gray-700",
	CANCELED: "bg-red-200 text-red-800"
};

function InvoiceStatusPill({ status }: { status?: string }) {
	const key = (status || "").toUpperCase();
	return (
		<span
			className={cn(
				"px-2.5 py-1 rounded-full text-xs font-semibold",
				STATUS_CLASSES[key] || "bg-gray-200 text-gray-700"
			)}
		>
			{STATUS_LABELS[key] || status || "—"}
		</span>
	);
}

const columnHelper = createColumnHelper<PublicContract>();

const columns = [
	columnHelper.display({
		id: "title",
		cell: ({ row }) => {
			const buyer = getPublicContractBuyerName(row.original);
			return (
				<div className="inline-flex items-center gap-3 min-w-0">
					<div className="size-12 rounded-lg bg-primary/10 grid place-content-center shrink-0">
						<FileText className="size-5 text-primary" />
					</div>
					<div className="min-w-0">
						<div className="font-medium truncate">{row.original.title || "Untitled invoice"}</div>
						<div className="text-xs text-muted-foreground truncate">
							{getPublicContractCode(row.original) || row.original.id}
							{buyer ? ` · ${buyer}` : ""}
						</div>
					</div>
				</div>
			);
		}
	}),
	columnHelper.display({
		id: "amount",
		cell: ({ row }) => {
			const total = getPublicContractTotal(row.original);
			return (
				<div className="text-sm font-medium">
					{total != null ? formatCurrency(total, "NGN") : "—"}
				</div>
			);
		}
	}),
	columnHelper.display({
		id: "status",
		cell: ({ row }) => (
			<div className="flex items-center justify-end gap-2">
				<InvoiceStatusPill status={row.original.status} />
				<ChevronRight className="h-4 w-4 text-muted-foreground" />
			</div>
		)
	})
];

const tabOptions: TabOption[] = [
	{ value: "", label: "All" },
	{ value: "PENDING", label: "Pending" },
	{ value: "PAID", label: "Paid" },
	{ value: "EXPIRED", label: "Expired" },
	{ value: "CANCELED", label: "Cancelled" }
];

function invoiceMatchesSearch(contract: PublicContract, term: string) {
	const buyer = getPublicContractBuyerInfo(contract);
	const haystacks = [
		contract.title,
		contract.description,
		getPublicContractCode(contract),
		contract.id,
		getPublicContractBuyerName(contract),
		buyer?.email,
		buyer?.firstName,
		buyer?.lastName,
		buyer?.phoneNumber,
		contract.status
	];
	return haystacks.some(value => String(value || "").toLowerCase().includes(term));
}

function RouteComponent() {
	const navigate = useNavigate();
	const { filters } = useFilters(Route.id);
	const [activeTab, setActiveTab] = useState<string>(filters.status ?? "");
	const [searchInput, setSearchInput] = useState(filters.q ?? "");
	const debouncedSearch = useDebounce(searchInput.trim(), 400);

	const query = usePublicContracts({
		page: (filters.pageIndex ?? 0) + 1,
		limit: filters.pageSize ?? 10,
		status: activeTab || undefined,
		search: debouncedSearch || undefined
	});

	const paginationState = {
		...filters,
		pageIndex: filters.pageIndex,
		pageSize: filters.pageSize
	};
	const sortingState = sortByToState(filters.sortBy);

	const setFilters = (partialFilters: Partial<typeof filters>) => {
		navigate({
			search: sanitizeParams({
				...filters,
				...partialFilters
			}) as any
		});
	};

	useEffect(() => {
		const next = debouncedSearch || undefined;
		if ((filters.q || undefined) === next)
			return;
		setFilters({ q: next, pageIndex: 0 });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedSearch]);

	const rows = useMemo(() => {
		const items = query.data?.items ?? [];
		const term = searchInput.trim().toLowerCase();
		if (!term)
			return items;
		return items.filter(contract => invoiceMatchesSearch(contract, term));
	}, [query.data, searchInput]);

	return (
		<div className="mx-auto flex w-full min-w-0 max-w-full flex-col space-y-4">
			<div className="mt-1 flex flex-col gap-3 sm:mt-4 sm:flex-row sm:items-center sm:justify-between">
				<h1 className="text-lg font-semibold sm:text-xl">Invoices</h1>
				<Button asChild className="w-full sm:w-auto">
					<Link to="/vendor/invoices/create">
						<Plus className="h-4 w-4" />
						Create invoice
					</Link>
				</Button>
			</div>

			<div className="space-y-3 md:hidden">
				<Input
					id="invoice-search-mobile"
					name="invoice-search"
					type="search"
					autoComplete="off"
					placeholder="Search invoices..."
					value={searchInput}
					onChange={e => setSearchInput(e.target.value)}
					className="w-full bg-white"
				/>
				<div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
					{tabOptions.map(tab => (
						<button
							key={tab.value || "all"}
							type="button"
							className={cn(
								"shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium",
								activeTab === tab.value
									? "border-primary bg-primary text-white"
									: "border-transparent bg-white text-muted-foreground"
							)}
							onClick={() => {
								setActiveTab(tab.value);
								setFilters({ status: tab.value || undefined, pageIndex: 0 });
							}}
						>
							{tab.label}
						</button>
					))}
				</div>
				{query.isPending
					? (
							<div className="grid place-content-center py-16">
								<Loader2 className="h-8 w-8 animate-spin text-primary" />
							</div>
						)
					: rows.length === 0
						? (
								<div className="rounded-xl border bg-white px-4 py-10 text-center text-sm text-muted-foreground">
									No invoices found.
								</div>
							)
						: (
								<div className="space-y-2">
									{rows.map(contract => (
										<button
											key={contract.id}
											type="button"
											className="flex w-full items-center gap-3 rounded-xl border bg-white p-3 text-left"
											onClick={() => {
												navigate({
													to: "/vendor/invoices/$invoiceid",
													params: { invoiceid: contract.id }
												});
											}}
										>
											<div className="grid size-11 shrink-0 place-content-center rounded-lg bg-primary/10">
												<FileText className="size-5 text-primary" />
											</div>
											<div className="min-w-0 flex-1">
												<p className="truncate font-medium">
													{contract.title || "Untitled invoice"}
												</p>
												<p className="truncate text-xs text-muted-foreground">
													{getPublicContractCode(contract) || contract.id}
													{getPublicContractBuyerName(contract)
														? ` · ${getPublicContractBuyerName(contract)}`
														: ""}
												</p>
												<p className="mt-1 text-sm font-semibold">
													{(() => {
														const total = getPublicContractTotal(contract);
														return total != null ? formatCurrency(total, "NGN") : "—";
													})()}
												</p>
											</div>
											<div className="flex shrink-0 flex-col items-end gap-2">
												<InvoiceStatusPill status={contract.status} />
												<ChevronRight className="h-4 w-4 text-muted-foreground" />
											</div>
										</button>
									))}
								</div>
							)}
			</div>

			<div className="hidden min-w-0 md:block">
				<GenericDataTable
					columns={columns}
					data={rows}
					activeTab={activeTab}
					isLoading={query.isPending || query.isFetching}
					tabOptions={tabOptions}
					pagination={paginationState}
					onTabChange={(tab) => {
						setActiveTab(tab);
						setFilters({ status: tab || undefined, pageIndex: 0 });
					}}
					filterField="status"
					searchValue={searchInput}
					onSearchChange={setSearchInput}
					paginationOptions={{
						onPaginationChange: (pagination) => {
							setFilters(
								typeof pagination === "function"
									? pagination(paginationState)
									: pagination
							);
						},
						rowCount: query.data?.totalItems
					}}
					filters={filters}
					handleRowClick={(row) => {
						navigate({
							to: "/vendor/invoices/$invoiceid",
							params: { invoiceid: row.original.id }
						});
					}}
					onFilterChange={next => setFilters(next)}
					sorting={sortingState}
					onSortingChange={(updaterOrValue) => {
						const newSortingState
							= typeof updaterOrValue === "function"
								? updaterOrValue(sortingState)
								: updaterOrValue;
						setFilters({ sortBy: stateToSortBy(newSortingState) });
					}}
				/>
			</div>
		</div>
	);
}
